// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Justification: all values here come from generated code and are covered by a unit-test.
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import * as constraints from './constraints';
import { cascade, enumValidator, maxLength, range, regex } from './helpers';

// Validates a UniqueIdPart field value.
export const uniqueIdPart = (value: string | undefined, name: string) =>
    maxLength(value, constraints.uniqueIdPart.MaxLength!, name) ??
    regex(value, constraints.uniqueIdPart.Pattern!, `${name} must contain only 'a'-'z', '0'-'9', '-' and '.'.`);

// Validates a UniqueId field value.
export const uniqueId = (value: string | undefined, name: string) =>
    maxLength(value, constraints.uniqueId.MaxLength!, name) ??
    regex(value, constraints.uniqueId.Pattern!, `${name} must contain only 'a'-'z', '0'-'9', '-', '.' and ':'.`);

// Validates a Locale field value.
export const locale = (value: string | undefined, name: string) =>
    maxLength(value, constraints.locale.MaxLength!, name) ??
    regex(value, constraints.locale.Pattern!, `${name} must be a valid locale identity, e.g. en-US.`);

// Validates a DisplayName field value.
export const displayName = (value: string | undefined, name: string) =>
    maxLength(value, constraints.displayName.MaxLength!, name) ??
    regex(value, constraints.displayName.Pattern!, `${name} must start and end with non-white space character.`);

// Validates a Title field value.
export const title = (value: string | undefined, name: string) =>
    maxLength(value, constraints.title.MaxLength!, name) ??
    regex(value, constraints.title.Pattern!, `${name} must start and end with non-white space character.`);

// Validates an Description field value.
export const description = (value: string | undefined, name: string) =>
    maxLength(value, constraints.description.MaxLength!, name) ??
    regex(value, constraints.descriptionRegex, `${name} must start and end with non-white space character.`);

// Validates a ProfileName field value.
export const profileName = (value: string | undefined, name: string) =>
    maxLength(value, constraints.profileName.MaxLength!, name) ??
    regex(value, constraints.profileName.Pattern!, `${name} must be a valid identifier.`);

// Validates an Order field value.
export const order = (value: number | undefined, name: string) =>
    range(value, constraints.order.InclusiveMinimum!, constraints.order.InclusiveMaximum!, name);

// Validates a Duration field value.
export const duration = (value: number | undefined, name: string) =>
    range(value, constraints.duration.InclusiveMinimum!, constraints.duration.InclusiveMaximum!, name);

// Validates an Percentage field value.
export const percentage = (value: number | undefined, name: string) =>
    range(value, constraints.percentage.InclusiveMinimum!, constraints.percentage.InclusiveMaximum!, name);

// Validates a URL field value.
export const url = (value: string | undefined, name: string) =>
    maxLength(value, constraints.url.MaxLength!, name) ??
    regex(value, constraints.url.Pattern!, `${name} must be a valid URL starting with $/, http:// or https://.`);

// Validates an AbsoluteUrl field value.
export const absoluteUrl = (value: string | undefined, name: string) =>
    maxLength(value, constraints.url.MaxLength!, name) ??
    regex(value, constraints.absoluteUrl.Pattern!, `${name} must be a valid URL starting with http:// or https://.`);

// Validates an Email field value.
export const email = (value: string | undefined, name: string) =>
    maxLength(value, constraints.email.MaxLength!, name) ??
    regex(value, constraints.email.Pattern!, `${name} must be a valid email address.`);

// Validates a JWT token parameter value.
export const jwt = (value: string | undefined, name: string) =>
    maxLength(value, constraints.jwt.MaxLength!, name) ??
    regex(value, constraints.jwt.Pattern!, `${name} must be a valid JWT token.`);

// Validates an UUID parameter value.
export const uuid = (value: string | undefined, name: string) =>
    maxLength(value, constraints.uuid.MaxLength!, name) ??
    regex(value, constraints.uuid.Pattern!, `${name} must be a valid UUID.`);

// Validates a Base64Url encoded SHA256 parameter value.
export const base64UrlSha256 = (value: string | undefined, name: string) =>
    maxLength(value, constraints.base64UrlSha256.MaxLength!, name) ??
    regex(value, constraints.base64UrlSha256.Pattern!, `${name} must be a valid SHA256 hash.`);

// Validates an OTAC parameter value.
export const otacType = (value: string | undefined, name: string) =>
    maxLength(value, constraints.otac.MaxLength!, name) ??
    regex(value, constraints.otac.Pattern!, `${name} must be a valid OTAC.`);

// Validates shared link ID parameter value.
export const sharedLinkId = (value: string | undefined, name: string) =>
    regex(value, constraints.sharedLinkId.Pattern!, `${name} must be a valid shared link ID.`);

// Validates a mime type parameter value.
export const mimeType = (value: string | undefined, name: string) =>
    regex(value, constraints.mimeType.Pattern!, `${name} must be a valid mime type.`);

// Validates a DifficultyLevel field value.
export const difficultyLevel = enumValidator(constraints.difficultyLevel);

// Validates a CodeLanguage field value.
export const editorType = enumValidator(constraints.editorType);

// Validates a TerminalType field value.
export const terminalType = enumValidator(constraints.terminalType);

// Validates a QuestionType field value.
export const questionType = enumValidator(constraints.questionType);

// Validates an AuthProvider parameter value.
export const authProviderType = enumValidator(constraints.authProvider);

// Validates an AppName parameter value.
export const appName = (value: string | undefined, name: string) =>
    maxLength(value, constraints.appName.MaxLength!, name) ??
    regex(value, constraints.appName.Pattern!, `${name} must be a valid app name.`);

// Validates an AppFilePath parameter value.
export const appFilePath = (value: string | undefined, name: string) =>
    maxLength(value, constraints.appFilePath.MaxLength!, name) ??
    regex(value, constraints.appFilePath.Pattern!, `${name} must be a valid path of an app file.`);

// Validates a GitHub repository parameter value of the form `owner/repo`.
export const githubRepo = (value: string | undefined, name: string) =>
    regex(value, new RegExp('^[^/]+/[^/]+$'), `${name} must be a valid GitHub repository.`);

// Validates a course type value.
export const courseType = enumValidator(constraints.courseType);

// Validates a course platform value.
export const coursePlatform = enumValidator(constraints.coursePlatform);

// Validates a tag array.
export const tags = (value: string[] | undefined, name: string) =>
    range(value?.length, constraints.tags.MinItems ?? 0, constraints.tags.MaxItems!, `${name}.length`) ??
    cascade(value, (tag) => regex(tag, constraints.tagName.Pattern!, name));

// Validates an abuse category value.
export const abuseCategory = enumValidator(constraints.abuseCategory);
